import { Button, DeleteIcon, Select, SelectOptionType } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { SingleValue } from 'react-select';
import styles from '../../Constraints.module.scss';
import { PriorityIconSelect } from './PriorityIconSelect';

/** Loosely-typed form values: the teacher/subject conditions forms are flat string-keyed blobs. */
type GenericFormValues = Record<string, number | string | boolean | null | undefined>;

type NumberPriorityConstraintItemProps = {
  label: string;
  /** Formik field name of the numeric value. */
  numberName: string;
  numberOptions: SelectOptionType[];
  /** Formik field name of the (optional) priority value; omit for fields without a priority. */
  priorityName?: string;
  tooltipText?: string;
  /** When set, renders a remove button that takes this constraint out of the form. */
  onRemove?: () => void;
};

/**
 * Generic "clearable number + optional priority" form row, shared by the teacher and subject
 * conditions forms so each flat field doesn't need its own bespoke FormItem clone.
 */
export const NumberPriorityConstraintItem = ({
  label,
  numberName,
  numberOptions,
  priorityName,
  tooltipText,
  onRemove,
}: NumberPriorityConstraintItemProps) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<GenericFormValues>();

  const numberValue = values[numberName];

  return (
    <div className={styles['condition-form-item']}>
      <div className={styles['row-wrapper']}>
        <div className='tks__row row-xs-11 mr-5' style={{ flex: 1 }}>
          <div className='tks__col col-xs-12'>
            <Select
              isClearable
              name={numberName}
              options={numberOptions}
              value={numberValue != null ? (numberOptions.find((o) => o.value === numberValue) ?? null) : null}
              onChange={(value) => {
                const opt = value as SingleValue<SelectOptionType>;
                setFieldTouched(numberName, true);
                setFieldValue(numberName, opt ? (opt.value as number) : null);
              }}
              label={label}
              tooltipText={tooltipText}
            />
          </div>
        </div>
        {priorityName && (
          <div className='tks__col col-xs-1'>
            <PriorityIconSelect name={priorityName} />
          </div>
        )}
        {onRemove && (
          <div className={styles['button-wrapper']}>
            <Button name='remove' hierarchy='secondary' onClick={onRemove} icon={<DeleteIcon />} />
          </div>
        )}
      </div>
    </div>
  );
};
